import { types as t, getRoot } from 'mobx-state-tree'
import globalEmissionsCountries from '../../../datasets/global-CIRCLES-2020-emission-sector-country.csv'
import { CountryCell, EmissionCountry } from '../types'
import { capitalize, take, sortBy } from 'lodash-es'
import { PALETTE_GRADIENT_TO_WHITE, LULUCF, Challenge } from '../../../lib/constants'
import { scalePow, pack, hierarchy, extent } from 'd3'

export const CountriesModel = t
  .model('CountriesModel', {
    dataset: t.frozen(globalEmissionsCountries as EmissionCountry[]),
    selectedCountryIndex: t.maybeNull(t.number),
  })
  .views((self) => ({
    get root() {
      return getRoot(self) as any
    },
  }))
  .views((self) => ({
    get emissionsForChallenge() {
      return self.dataset
        .filter((d) => d.sector === self.root.Circles.challenge)
        .filter((d) => (d.emission_mmt_co2 as any) !== 'NA')
    },
    get emissionsForLulucf() {
      return self.dataset.filter((d) => (d.sector as any) === LULUCF)
    },
  }))
  .views((self) => ({
    get minMax() {
      return extent(self.emissionsForChallenge.map((d) => d.emission_mmt_co2))
    },
  }))
  .views((self) => ({
    get colorScale() {
      const [color1, color2] = PALETTE_GRADIENT_TO_WHITE[self.root.Circles.challenge]
      const [min, max] = self.minMax
      const colorScale = scalePow().exponent(0.4).domain([min, max]).range([color2, color1])
      return (d: number) => colorScale(d)
    },
  }))
  .views((self) => ({
    get countriesHierarchy() {
      const emissionsForChallenge = self.emissionsForChallenge

      const children = emissionsForChallenge.map((d) => ({
        ...d,
        color: self.colorScale(d.emission_mmt_co2),
      }))

      return hierarchy({
        name: 'world',
        sector: '' as Challenge,
        emission_mmt_co2: 0,
        emission_percentage: 0,
        color: '',
        country: '',
        country_code: '',
        children,
      })
    },
  }))
  .views((self) => ({
    get countries(): CountryCell[] {
      const countriesHierarchy = self.countriesHierarchy
        .sum((d: any) => d.emission_mmt_co2)
        .sort(((a: any, b: any) => (a.value < b.value ? 1 : -1)) as any)

      const CIRCLE_RADIUS = 150 // FIXME: The original value is in the voronoi component!

      const doPack = pack().size([CIRCLE_RADIUS * 2, CIRCLE_RADIUS * 2])
      doPack(countriesHierarchy)

      return countriesHierarchy.leaves()
    },
  }))
  .views((self) => ({
    get countriesTopFive(): CountryCell[] {
      const sorted = sortBy(self.countries, (t) => -t.data.emission_mmt_co2)
      const top = take(sorted, 5)
      return top
    },
  }))
  .views((self) => ({
    get countriesAlphabetical() {
      return sortBy(self.countries, 'data.country')
    },
  }))
  .views((self) => ({
    get selectedCountry() {
      return self.countries[self.selectedCountryIndex] ?? null
    },
  }))
  .views((self) => ({
    get selectedLulucf() {
      return self.emissionsForLulucf.find(
        (e) => e.country_code === self.selectedCountry?.data.country_code
      )
    },
  }))
  .views((self) => ({
    get hasLulucf() {
      const challenge = (getRoot(self) as any).Circles.challenge
      return (
        self.selectedLulucf !== undefined &&
        self.selectedLulucf.emission_mmt_co2 !== 0 &&
        challenge === 'agriculture'
      )
    },
  }))
  .actions((self) => ({
    setSelectedCountry(country: CountryCell | null) {
      const index = self.countries.indexOf(country)
      self.selectedCountryIndex = index === -1 ? null : index
    },
  }))
